/**
 * `<Tabs>` — minimal headless tab primitive (controlled).
 *
 * Wave 1.5 promotion. Consolidates the inline tab-strip TeamPage shipped
 * for the Cycles / Members split. URL-search-param wiring stays in
 * TeamPage — the primitive is purely the visual + a11y shell:
 *
 *   - `<Tabs value onValueChange>` — controlled value carrier; provides
 *     context to triggers + panels.
 *   - `<TabsList>` — the underlined nav strip with `role="tablist"`.
 *   - `<TabsTrigger value>` — a button rendered inside TabsList; sets
 *     `aria-selected` based on context, fires `onValueChange(value)`.
 *   - `<TabsContent value>` — the panel; renders only when `value`
 *     matches; `role="tabpanel"`.
 */
import { type ReactNode, createContext, useContext } from "react";

import { cn } from "../../lib/cn";
import { SlidingIndicator } from "./SlidingIndicator";

interface TabsContextValue {
  value: string;
  onValueChange: (next: string) => void;
}

const TabsContext = createContext<TabsContextValue | null>(null);

function useTabsContext(component: string): TabsContextValue {
  const ctx = useContext(TabsContext);
  if (!ctx) {
    throw new Error(`<${component}> must be rendered inside <Tabs>`);
  }
  return ctx;
}

export interface TabsProps {
  value: string;
  onValueChange: (next: string) => void;
  children: ReactNode;
  className?: string;
}

export function Tabs({ value, onValueChange, children, className }: TabsProps) {
  return (
    <TabsContext.Provider value={{ value, onValueChange }}>
      <div className={cn("flex flex-col gap-4", className)}>{children}</div>
    </TabsContext.Provider>
  );
}

export interface TabsListProps {
  children: ReactNode;
  /** Optional accessible label, surfaced on the `<nav>` for screen readers. */
  "aria-label"?: string;
  className?: string;
}

export function TabsList({ children, className, "aria-label": ariaLabel }: TabsListProps) {
  return (
    <nav
      className={cn("relative isolate flex gap-1 border-b border-border", className)}
      role="tablist"
      aria-label={ariaLabel}
    >
      {/* #2172 — the active underline glides between tabs. */}
      <SlidingIndicator shape="underline" />
      {children}
    </nav>
  );
}

export interface TabsTriggerProps {
  value: string;
  children: ReactNode;
  className?: string;
  "data-testid"?: string;
}

export function TabsTrigger({
  value,
  children,
  className,
  "data-testid": testId,
}: TabsTriggerProps) {
  const ctx = useTabsContext("TabsTrigger");
  const active = ctx.value === value;
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      onClick={() => ctx.onValueChange(value)}
      data-testid={testId}
      className={cn(
        "-mb-px inline-flex items-center gap-1.5 border-b-2 px-3 py-2 text-sm font-medium",
        // The active underline is the gliding `<SlidingIndicator/>` above.
        active
          ? "border-transparent text-fg"
          : "border-transparent text-fg-muted hover:border-border-strong hover:text-fg",
        className,
      )}
    >
      {children}
    </button>
  );
}

export interface TabsContentProps {
  value: string;
  children: ReactNode;
  className?: string;
  /** Optional accessible label for the panel. */
  "aria-label"?: string;
}

export function TabsContent({
  value,
  children,
  className,
  "aria-label": ariaLabel,
}: TabsContentProps) {
  const ctx = useTabsContext("TabsContent");
  if (ctx.value !== value) return null;
  return (
    <section
      key={value}
      role="tabpanel"
      aria-label={ariaLabel}
      // #2172 — a newly chosen panel rises in.
      className={cn("animate-rise-in", className)}
    >
      {children}
    </section>
  );
}
